import { memberDisplayName, type OrgMember, type WorkspaceGrant } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';
import { Notice } from '../../../shared/ui/notice';
import { GrantsEditor } from './ui';

/** The per-member grants editor of the Members tab: "All workspaces" plus every workspace of the org. */
export function ManageGrantsDialog({
  org,
  member,
  grants,
  workspaces,
  onOpenChange,
}: {
  org: { id: string };
  /** null keeps the dialog closed. */
  member: OrgMember | null;
  grants: readonly WorkspaceGrant[];
  workspaces: readonly { id: string; name: string; archived_at: string | null }[];
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('member');
  const name = member ? memberDisplayName(member) : '';
  const fullAccess = member?.role === 'owner' || member?.role === 'admin';
  return (
    <Dialog open={member !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('grants.title', { name })}</DialogTitle>
          <DialogDescription>{t('grants.description')}</DialogDescription>
        </DialogHeader>
        {fullAccess ? <Notice>{t('grants.throughOrgRole', { name })}</Notice> : null}
        {member ? (
          <GrantsEditor
            org={org}
            member={member}
            grants={grants}
            targets={[
              { id: null, name: t('grants.all'), archived: false },
              ...workspaces.map((w) => ({
                id: w.id,
                name: w.name,
                archived: w.archived_at !== null,
              })),
            ]}
          />
        ) : null}
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>{t('grants.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
